import React, { ReactNode, useEffect, useState } from 'react'
import {
  Button,
  Divider,
  GridContainer,
  GridItem,
  Heading,
  Layout,
  Spinner,
  Tabs,
} from '@enterprise-ui/canvas-ui-react'
import { ReportSideFilters } from './ReportSideFilters'
import { DateFilter, SideFilter } from './ReportContext/ReportContext'
import ReportTable from './ReportTable'
import { ColDef } from 'ag-grid-community'
import GraphView from './GraphView'
import { Value } from '../Cases/Search/SearchContext/SearchInitialValues'
import { CSVLink } from 'react-csv'
import { RangeFilter } from './RangeFilter'
import { FILTER_IDS } from './ReportConstants'

/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
  ChartBarVerticalIcon,
  DataTableIcon,
  DownloadIcon,
  EnterpriseIcon,
} from '@enterprise-ui/icons'

const baseLayout = css`
  height: 80vh;
  overflow: hidden;
`

export enum VisualizationTypes {
  TABLE = 'table',
  GRAPH = 'graph',
}

export interface DynamicFilter {
  enabled: boolean
  filterName: string
  id: string
  handleDynamicFilterChange?: any
}

export interface ReportBaseLayoutProps {
  reportName: string
  loading?: boolean
  visualizationTypes?: VisualizationTypes[]
  sideFilters?: SideFilter[]
  data: any[]
  chartData: any[]
  uniqueSubTypes: any[]
  columns: ColDef[]
  customSideFilters?: ReactNode
  reportType: 'aggregation' | 'Comparison' | 'workflow_management'
  dynamicFilter?: DynamicFilter
  dateFilter: DateFilter
  handleQueryChange: any
  buildDrillDownUrl?: any
  onSubReasonFilterChange?: (
    activeSubReasonFilters: string[],
    activeReasonCodeFilters: string[],
  ) => void
}

export const ReportBaseLayout: React.FC<ReportBaseLayoutProps> = ({
  reportName,
  loading = false,
  visualizationTypes = [VisualizationTypes.TABLE, VisualizationTypes.GRAPH],
  sideFilters = [],
  data,
  columns,
  reportType,
  chartData,
  uniqueSubTypes,
  customSideFilters,
  dynamicFilter,
  dateFilter,
  buildDrillDownUrl,
  onSubReasonFilterChange,
}) => {
  const [showFilters, setShowFilters] = useState<boolean>(true)
  const [activeTab, setActiveTab] = useState('tab_01')
  const [gridData, setGridData] = useState(data)
  const [dynamicFilters, setDynamicFilters] = useState<Value[]>([])
  const [activeUniqueSubTypes, setActiveUniqueSubTypes] = useState<any[]>([])
  const [csvHeaders, setCsvHeaders] = useState<any[]>([])
  const [subReasonFilters, setSubReasonFilters] = useState<Value[]>([])
  const [activeReasonCodeFilters, setActiveReasonCodeFilters] = useState<
    string[]
  >([])
  const [activeSubReasonCodeFilters, setActiveSubReasonCodeFilters] = useState<
    string[]
  >([])

  useEffect(() => {
    // Display the data as received from parent component
    setGridData([...data])
    setCsvHeaders(
      columns.map((column) => {
        return { key: column.field, label: column.headerName ?? column.field }
      }),
    )
  }, [data, columns])

  // Update sub reason filters when sideFilters change
  useEffect(() => {
    const subReasonFilter = sideFilters.find(
      (f) => f.id === FILTER_IDS.SUB_REASON_CODE,
    )
    if (subReasonFilter?.values) {
      setSubReasonFilters(subReasonFilter.values.map((v) => ({ ...v })))
    }
  }, [sideFilters])

  useEffect(() => {
    // Use actual reason codes from API response (uniqueSubTypes)
    setDynamicFilters(
      uniqueSubTypes.map((filter) => {
        const date: Value = {
          id: filter,
          label: filter,
          value: filter,
          isChecked: false,
        }
        return date
      }),
    )
    setActiveUniqueSubTypes(uniqueSubTypes)
  }, [uniqueSubTypes])

  // Apply both reason code and sub reason code filters
  const applyFilters = (
    reasonCodeFilters: string[],
    subReasonCodeFilters: string[],
  ) => {
    let filteredData = [...data]

    // First apply reason code filter (filter by rows)
    if (reasonCodeFilters.length > 0) {
      filteredData = filteredData.filter((row) =>
        reasonCodeFilters.includes(row[dynamicFilter?.id ?? 'aggregation']),
      )
    }

    // Sub reason code filtering is now handled in AggregatedReport's generateGridData
    // Just filter out rows that don't have matching sub reason codes if filter is active
    if (subReasonCodeFilters.length > 0) {
      filteredData = filteredData.filter((row) => {
        if (row.subReasonCodes && Array.isArray(row.subReasonCodes)) {
          return row.subReasonCodes.some((code: string) =>
            subReasonCodeFilters.includes(code),
          )
        }
        return true
      })
    }

    setGridData(filteredData)
  }

  // Handler for reason code filter - filters rows
  const handleDynamicFilterChange = (
    id: string,
    type: string,
    event: any,
    searchField?: string,
  ) => {
    const filterUpdate = dynamicFilters.map((value) => {
      if (value.value === event.target.value) {
        return { ...value, isChecked: !value.isChecked }
      } else return { ...value }
    })

    const activeFilters = filterUpdate
      .filter((value) => value.isChecked)
      .map((value) => value.value as string)

    setDynamicFilters(filterUpdate)
    setActiveReasonCodeFilters(activeFilters)

    if (activeFilters.length > 0) {
      setActiveUniqueSubTypes(activeFilters)
    } else {
      setActiveUniqueSubTypes(uniqueSubTypes)
    }

    // Notify parent to regenerate grid data with both filters
    if (onSubReasonFilterChange) {
      onSubReasonFilterChange(activeSubReasonCodeFilters, activeFilters)
    }

    // Apply both filters together
    applyFilters(activeFilters, activeSubReasonCodeFilters)
  }

  // Handler for sub reason code filter (and any other dynamic side filters)
  // Filters are client-side only, no URL updates
  const handleSideFilterDynamicChange = (
    id: string,
    type: string,
    event: any,
    searchField?: string,
  ) => {
    if (id === FILTER_IDS.SUB_REASON_CODE) {
      // Update sub reason filter state
      const filterUpdate = subReasonFilters.map((value) => {
        if (value.value === event.target.value) {
          return { ...value, isChecked: !value.isChecked }
        } else return { ...value }
      })
      setSubReasonFilters(filterUpdate)

      const activeFilters = filterUpdate
        .filter((value) => value.isChecked)
        .map((value) => value.value as string)

      setActiveSubReasonCodeFilters(activeFilters)

      // Notify parent to regenerate grid data with both filters
      if (onSubReasonFilterChange) {
        onSubReasonFilterChange(activeFilters, activeReasonCodeFilters)
      }

      // Apply both filters together
      applyFilters(activeReasonCodeFilters, activeFilters)
    }
  }

  // Attach handlers and updated state to sideFilters with source: 'dynamic'
  const processedSideFilters = sideFilters.map((filter) => {
    if (filter.source === 'dynamic') {
      return {
        ...filter,
        values:
          filter.id === FILTER_IDS.SUB_REASON_CODE
            ? subReasonFilters
            : filter.values,
        handleDynamicFilterChange: handleSideFilterDynamicChange,
      }
    }
    return filter
  })

  const toggleFilters = () => {
    setShowFilters((filter) => !filter)
  }

  return (
    <>
      <Layout.SectionHeader>
        <GridContainer justify="space-between">
          <GridItem>
            <Heading size={2}>{reportName}</Heading>
          </GridItem>
          <GridItem>
            <GridContainer>
              <GridItem>
                <RangeFilter dateFilter={dateFilter} />
              </GridItem>
              <GridItem>
                <CSVLink
                  data={gridData}
                  filename={`${reportName}-${new Date()}.csv`}
                  headers={csvHeaders}
                >
                  <Button iconOnly aria-label="Download">
                    <EnterpriseIcon icon={DownloadIcon} />
                  </Button>
                </CSVLink>
              </GridItem>
              <GridItem>
                <Button type="primary" onClick={toggleFilters}>
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </Layout.SectionHeader>

      <Layout.Body includeRail={!showFilters}>
        <Tabs
          activeTab={activeTab}
          onTabSelect={(event: any, tab: any) => {
            setActiveTab(tab.name)
          }}
        >
          {visualizationTypes.includes(VisualizationTypes.TABLE) && (
            <Tabs.Item name="tab_01">
              <p>
                <EnterpriseIcon icon={DataTableIcon} inline />
                &nbsp; Table
              </p>
            </Tabs.Item>
          )}
          {visualizationTypes.includes(VisualizationTypes.GRAPH) && (
            <Tabs.Item name="tab_02">
              <p>
                <EnterpriseIcon icon={ChartBarVerticalIcon} inline />
                &nbsp; Graph
              </p>
            </Tabs.Item>
          )}
          <Divider />
          {visualizationTypes.includes(VisualizationTypes.TABLE) && (
            <Tabs.Content name="tab_01" className="hc-ph-none">
              {!loading ? (
                <ReportTable data={gridData} columns={columns} />
              ) : (
                <GridContainer justify="center" align="center" css={baseLayout}>
                  <GridItem>
                    <Spinner />
                  </GridItem>
                </GridContainer>
              )}
            </Tabs.Content>
          )}
          {visualizationTypes.includes(VisualizationTypes.GRAPH) && (
            <Tabs.Content name="tab_02">
              {!loading ? (
                <GraphView
                  reportType={reportType}
                  chartData={chartData}
                  uniqueSubType={activeUniqueSubTypes}
                  buildDrillDownUrl={buildDrillDownUrl}
                />
              ) : (
                <GridContainer justify="center" align="center" css={baseLayout}>
                  <GridItem>
                    <Spinner />
                  </GridItem>
                </GridContainer>
              )}
            </Tabs.Content>
          )}
        </Tabs>
      </Layout.Body>

      {showFilters && (
        <Layout.Rail>
          <ReportSideFilters
            sideFilters={processedSideFilters}
            dynamicFilter={
              dynamicFilter && {
                ...dynamicFilter,
                handleDynamicFilterChange,
              }
            }
            dynamicFilters={dynamicFilters}
            customSideFilters={customSideFilters}
          />
        </Layout.Rail>
      )}
    </>
  )
}
